import React, { useState, useEffect, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import axiosInstance from 'utils/axiosUtil';
import { DateUtil } from 'doctivity-shared/utils';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';
import { DynamicTable } from './DynamicTable';

const DEFAULT_QUERY = { offset: 0, limit: 25, order: [['last_name', 'ASC']] };

/**
 * Recipient picker for "specific users" / "everyone in a client". Server-side
 * search (name/email), filter by client and hide-admins, paginated for
 * production scale via the shared DynamicTable. Click rows to toggle (muted-green
 * highlight); "Select all matching" grabs every user matching the current
 * filters (that's how you pick a whole client). The running selection can be
 * reviewed/trimmed via "Selected only", and the count is always shown.
 */
const UserPickerDialog = ({ open, initialSelected, clients, onClose, onConfirm }) => {
  const [selected, setSelected] = useState([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [clientId, setClientId] = useState('');
  const [hideAdmins, setHideAdmins] = useState(false);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [query, setQuery] = useState(DEFAULT_QUERY);

  const rowsPerPage = query.limit || 25;
  const pageIndex = Math.floor((query.offset || 0) / rowsPerPage);

  useEffect(() => {
    if (open) {
      setSelected(initialSelected || []);
      setQ('');
      setQDebounced('');
      setClientId('');
      setHideAdmins(false);
      setSelectedOnly(false);
      setQuery(DEFAULT_QUERY);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Any filter/mode change goes back to the first page.
  useEffect(() => {
    setQuery((prev) => ({ ...prev, offset: 0 }));
  }, [qDebounced, clientId, hideAdmins, selectedOnly]);

  // Fetch the current page from the server (skipped in "Selected only" review mode).
  useEffect(() => {
    if (!open || selectedOnly) return undefined;
    let active = true;
    setLoading(true);
    const params = { limit: query.limit, offset: query.offset };
    if (qDebounced) params.q = qDebounced;
    if (clientId) params.clientId = clientId;
    if (hideAdmins) params.hideAdmins = 'true';
    axiosInstance
      .get('/v2/messages/user-options', { params })
      .then((res) => {
        if (!active) return;
        setRows(res.data?.rows || []);
        setCount(res.data?.count || 0);
      })
      .catch(() => {
        if (!active) return;
        setRows([]);
        setCount(0);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, selectedOnly, qDebounced, clientId, hideAdmins, query]);

  // What the table shows: server results normally, or the selected set (client-side
  // paginated + name-filtered) in review mode.
  const displayed = useMemo(() => {
    if (!selectedOnly) return { rows, count, loading };
    const term = qDebounced.trim().toLowerCase();
    const filtered = term
      ? selected.filter((u) => `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(term))
      : selected;
    const start = pageIndex * rowsPerPage;
    return { rows: filtered.slice(start, start + rowsPerPage), count: filtered.length, loading: false };
  }, [selectedOnly, rows, count, loading, selected, qDebounced, pageIndex, rowsPerPage]);

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const params = { all: 'true' };
      if (qDebounced) params.q = qDebounced;
      if (clientId) params.clientId = clientId;
      if (hideAdmins) params.hideAdmins = 'true';
      const res = await axiosInstance.get('/v2/messages/user-options', { params });
      const allRows = res.data?.rows || [];
      setSelected((prev) => {
        const byId = new Map(prev.map((u) => [u.id, u]));
        allRows.forEach((u) => byId.set(u.id, u));
        return Array.from(byId.values());
      });
    } catch (e) {
      /* ignore */
    } finally {
      setSelectingAll(false);
    }
  };

  const columns = [
    { label: 'First Name', key: 'first_name', sortable: false, filter: false },
    { label: 'Last Name', key: 'last_name', sortable: false, filter: false },
    { label: 'Email', key: 'email', sortable: false, filter: false },
    { label: 'Client', key: 'client_names', sortable: false, filter: false },
    {
      label: 'Last Login',
      key: 'last_login',
      sortable: false,
      filter: false,
      format: (v) => (v ? DateUtil.formatFromDB(v) : 'Never'),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Select Recipients</DialogTitle>
      <DialogContent dividers>
        {/* Selection summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant='subtitle2'>{selected.length} selected</Typography>
          {selected.length > 0 && (
            <Button size='small' color='inherit' onClick={() => setSelected([])}>Clear</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <FormControlLabel
            control={<Checkbox size='small' checked={selectedOnly} onChange={(e) => setSelectedOnly(e.target.checked)} />}
            label='Selected only'
            disabled={selected.length === 0}
          />
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', my: 1 }}>
          <TextField
            label={selectedOnly ? 'Search selected' : 'Search name or email'}
            size='small'
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
          />
          {!selectedOnly && (
            <>
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <InputLabel id='user-picker-client'>Client</InputLabel>
                <Select
                  labelId='user-picker-client'
                  label='Client'
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  <MenuItem value=''>All clients</MenuItem>
                  {(clients || []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox checked={hideAdmins} onChange={(e) => setHideAdmins(e.target.checked)} />}
                label='Hide admins'
              />
              <Button
                variant='outlined'
                size='small'
                onClick={selectAllMatching}
                disabled={count === 0 || loading || selectingAll}
              >
                {`Select all${count ? ` ${count}` : ''}`}
              </Button>
            </>
          )}
        </Box>

        {/* Results — shared DynamicTable, multi-select (muted-green highlight). */}
        <DynamicTable
          columns={columns}
          idKey='id'
          query={query}
          data={displayed}
          onQueryChange={setQuery}
          multiSelectedRows={selected}
          onMultiSelect={(newRows) => setSelected([...newRows])}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>Cancel</Button>
        <Button onClick={() => onConfirm(selected)} variant='contained' color='primary'>
          {`Add ${selected.length} recipient${selected.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UserPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialSelected: PropTypes.array,
  clients: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export { UserPickerDialog };
export default UserPickerDialog;
